import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { findElementByExternalIdAndSceneType } from "@/lib/stores/editor-store/editor-state";
import { useEffect, useMemo } from "react";
import TextElementEditor from "./TextElementEditor";
import ImageElementEditor from "./ImageElementEditor";
import VideoElementEditor from "./VideoElementEditor";
import RectangleElementEditor from "./RectangleElementEditor";
import { SceneTypeDto } from "@repo/api/overlays";
import { getCompositionTimings } from "@repo/video-engine-i2v/utils/timing";
import { useElementMediaDropzone } from "../ElementsList/element-list-hooks";
import AddElementBottomBar from "../ElementsList/AddElementBottomBar";
import {
  MediaUploadDragHover,
  MediaUploadingIndicator,
} from "../ElementsList/ElementsList";
import { NavigationButton } from "../../NavigationButton";
import { Button } from "@/lib/components/Button/Button";
import { IconEye, IconEyeOff, IconTrash } from "@tabler/icons-react";

type Props = {
  elementExternalId: string;
  sceneType: SceneTypeDto;
};

const ELEMENT_TYPE_LABELS: Record<string, string> = {
  TEXT: "Edit Text",
  IMAGE: "Edit Image",
  VIDEO: "Edit Video",
  RECTANGLE: "Edit Rectangle",
};

export default function ElementEditor({ elementExternalId, sceneType }: Props) {
  const updateOverlaysTabState = useEditorStore(
    (state) => state.service.updateOverlaysTabState,
  );
  const deleteElement = useEditorStore((state) => state.service.deleteElement);
  const toggleElementsVisibility = useEditorStore(
    (state) => state.service.toggleElementsVisibility,
  );

  const composition = useEditorStore((state) => state.video!.compositionJson);
  const animatedImages = useEditorStore((state) => state.animatedImages);

  const element = useEditorStore((state) =>
    findElementByExternalIdAndSceneType(state, elementExternalId, sceneType),
  );

  const { getRootProps, isDragActive, getInputProps, open, isUploadingMedia } =
    useElementMediaDropzone(sceneType);

  useEffect(() => {
    if (!element) {
      updateOverlaysTabState((state) => {
        state.currentElement = null;
      });
    }
  }, [element, updateOverlaysTabState]);

  const compositionTimings = useMemo(() => {
    return getCompositionTimings(composition, animatedImages);
  }, [composition, animatedImages]);

  if (!element) {
    return null;
  }

  return (
    <div className="relative flex h-full w-full flex-col" {...getRootProps()}>
      <NavigationButton
        text={ELEMENT_TYPE_LABELS[element.type] ?? "Edit Element"}
        onClick={() => {
          updateOverlaysTabState((state) => {
            state.currentElement = null;
          });
        }}
        className="py-4"
      >
        <Button
          size="iconSm"
          variant="outline"
          title={element.isHidden ? "Show element" : "Hide element"}
          onClick={() => toggleElementsVisibility([element.externalId])}
        >
          {element.isHidden ? (
            <IconEyeOff className="h-4 w-4" />
          ) : (
            <IconEye className="h-4 w-4" />
          )}
        </Button>
        <Button
          size="iconSm"
          variant="outline"
          onClick={() => deleteElement(element.externalId, sceneType)}
        >
          <IconTrash className="h-4 w-4" />
        </Button>
      </NavigationButton>
      <div className="w-full flex-1 overflow-y-auto pt-6 pb-16">
        {element.type === "TEXT" && (
          <TextElementEditor
            element={element}
            sceneType={sceneType}
            compositionTimings={compositionTimings}
          />
        )}
        {element.type === "IMAGE" && (
          <ImageElementEditor
            element={element}
            sceneType={sceneType}
            compositionTimings={compositionTimings}
          />
        )}
        {element.type === "VIDEO" && (
          <VideoElementEditor
            element={element}
            sceneType={sceneType}
            compositionTimings={compositionTimings}
          />
        )}
        {element.type === "RECTANGLE" && (
          <RectangleElementEditor
            element={element}
            sceneType={sceneType}
            compositionTimings={compositionTimings}
          />
        )}
      </div>

      <AddElementBottomBar
        getInputProps={getInputProps}
        open={open}
        sceneType={sceneType}
      />
      {isDragActive && <MediaUploadDragHover />}
      {isUploadingMedia && <MediaUploadingIndicator />}
    </div>
  );
}
